import type { EngineDocument } from "../scene/model";
import { isInteractive } from "../native/interactive";
import { bounds } from "../native/geometry";
import { effectBounds } from "../render/native-effects-raster";
import { NativeCanvasRenderer } from "../render/native-canvas";
import { ImageStore } from "../render/image-store";
import { textData } from "../native/text";
import { hasEffects } from "../native/effects";
import { shapeStyle } from "../native/appearance";
import { prepareFonts } from "../render/native-fonts";
import { exportFontAssets } from "./native-font-assets";
import {
  buildHTMLFromExportData,
  buildSavePhp,
  buildResultsPhp,
  buildEmptyResultsJson,
} from "./worksheet-html.js";
export interface ExportPage {
  name: string;
  data: Record<string, unknown>;
}
export interface NativeMaterial {
  pages: ExportPage[];
  assets: Map<string, Blob>;
}
export async function buildNativeMaterial(
  document: EngineDocument,
): Promise<NativeMaterial> {
  const images = new ImageStore();
  await images.prepare(document.assets);
  await prepareFonts(document);
  const assets = new Map<string, Blob>(),
    names = new Map<string, string>();
  const referenced = new Set(
    document.pages
      .flatMap((p) => p.nodes)
      .flatMap((n) => Object.values(n.media)),
  );
  for (const a of document.assets.filter((a) => referenced.has(a.id))) {
    const response = await fetch(a.source);
    if (!response.ok) throw new Error("Не удалось прочитать ресурс проекта.");
    const blob = await response.blob(),
      ext = blob.type.split("/")[1]?.split("+")[0] || "bin",
      name =
        (a.kind === "audio" ? "sounds/" : "images/") +
        `resource-${names.size + 1}.${ext}`;
    assets.set(name, blob);
    names.set(a.id, name.split("/")[1]!);
  }
  const fontCSS = await exportFontAssets(document, assets);
  const pages: ExportPage[] = [];
  for (const p of document.pages) {
    const elements: Record<string, unknown>[] = [];
    const scene = { ...document, activePageId: p.id };
    for (const [index, n] of p.nodes.entries()) {
      if (n.appearance.visible === false) continue;
      const base: Record<string, unknown> = {
        ...n.interaction,
        objectId: n.interaction.objectId || n.id,
        x: n.transform.x,
        y: n.transform.y,
        width: n.geometry.width,
        height: n.geometry.height,
        anchor: "center",
        angle: n.transform.rotation || 0,
        skewX: n.transform.skewX || 0,
        opacity: n.appearance.opacity ?? 1,
        blendMode: n.appearance.blendMode || "source-over",
        visible: true,
        zIndex: index + 1,
      };
      if (isInteractive(n)) {
        const style = shapeStyle(n);
        Object.assign(base, {
          targetScreenFileName:
            document.pages.find((q) => q.id === n.interaction.targetScreenId)
              ?.exportName || "",
          fill: style.fillEnabled
            ? rgba(style.fill, Number(n.appearance.fillAlpha ?? 1))
            : "transparent",
          stroke: style.strokeEnabled
            ? rgba(style.stroke, Number(n.appearance.strokeAlpha ?? 1))
            : "transparent",
          strokeWidth: style.strokeEnabled ? style.strokeWidth : 0,
          strokeDashArray:
            style.strokeDash ||
            (["dashed", "dotted"].includes(style.lineStyle) ? [4, 4] : null),
          rx: n.geometry.cornerRadius || 0,
          textFill: n.interaction.textColor,
          interactiveImageFileName: n.media.interactiveImage
            ? names.get(n.media.interactiveImage)
            : "",
          hoverSoundFileName: n.media.hoverSound
            ? names.get(n.media.hoverSound)
            : "",
          clickSoundFileName: n.media.clickSound
            ? names.get(n.media.clickSound)
            : "",
          releaseSoundFileName: n.media.releaseSound
            ? names.get(n.media.releaseSound)
            : "",
        });
        if (
          !(
            hasEffects(n) ||
            n.geometry.warpCorners ||
            n.layer.clipToBelow ||
            style.lineStyle === "hand"
          ) ||
          n.interaction.role === "inputField"
        ) {
          if (
            n.interaction.role === "inputField" &&
            (hasEffects(n) ||
              n.geometry.warpCorners ||
              n.layer.clipToBelow ||
              style.lineStyle === "hand")
          )
            base.nativeLive = { nodeId: n.id, box: liveBox(n) };
          elements.push(base);
          continue;
        }
      } else if (
        n.kind === "text" &&
        n.interaction.dynamicTextType &&
        n.interaction.dynamicTextType !== "none"
      ) {
        const t = textData(n);
        if (n.geometry.warpCorners || n.layer.clipToBelow || hasEffects(n))
          base.nativeLive = { nodeId: n.id, box: liveBox(n) };
        elements.push({
          ...base,
          role: "decorText",
          anchor: "center",
          text: t.value,
          fontSize: t.size,
          fontFamily: t.family,
          fontWeight: t.bold ? "bold" : "normal",
          fontStyle: t.italic ? "italic" : "normal",
          textAlign: t.align,
          fill: t.color,
        });
        continue;
      }
      {
        const b = hasEffects(n) ? effectBounds(n) : bounds(n),
          pad =
            n.kind === "text"
              ? 12
              : n.kind === "path"
                ? Math.max(12, (n.appearance.strokeWidth || 0) * 5)
                : 2;
        const box = {
          x: Math.floor(b.x - pad),
          y: Math.floor(b.y - pad),
          width: Math.ceil(b.width + pad * 2),
          height: Math.ceil(b.height + pad * 2),
        };
        if (box.width * box.height * 4 > 64000000)
          throw new Error("Объект слишком велик для экспорта 2×.");
        const canvas = globalThis.document.createElement("canvas"),
          r = new NativeCanvasRenderer(canvas, images);
        r.draw(
          {
            document: scene,
            camera: { x: -box.x * 2, y: -box.y * 2, zoom: 2 },
            width: box.width * 2,
            height: box.height * 2,
            dpr: 1,
          },
          true,
          new Set([n.id]),
        );
        const blob = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (b) =>
              b
                ? resolve(b)
                : reject(new Error("Ошибка экспорта изображения.")),
            "image/png",
          ),
        );
        r.dispose();
        const file = `art-${pages.length + 1}-${index + 1}.png`;
        assets.set("images/" + file, blob);
        elements.push({
          ...base,
          role: isInteractive(n) ? n.interaction.role : "decorImage",
          x: isInteractive(n) ? box.x + box.width / 2 : box.x,
          y: isInteractive(n) ? box.y + box.height / 2 : box.y,
          width: box.width,
          height: box.height,
          anchor: isInteractive(n) ? "center" : "topLeft",
          angle: 0,
          skewX: 0,
          opacity: 1,
          imageFileName: file,
          ...(isInteractive(n)
            ? { interactiveImageEnabled: true, interactiveImageFileName: file }
            : {}),
        });
      }
    }
    pages.push({
      name: p.exportName,
      data: {
        version: 1,
        native: true,
        fontCSS,
        projectId: document.extensions.projectId || p.id,
        screenId: p.id,
        screenName: p.name,
        fileName: p.exportName,
        width: p.size.width,
        height: p.size.height,
        background: p.backgroundVisible ? "#ffffff" : "transparent",
        elements,
        ...(elements.some((el) => el.nativeLive)
          ? {
              liveDocument: {
                ...scene,
                pages: [p],
                assets: scene.assets
                  .filter((a) => names.has(a.id))
                  .map((a) => ({
                    ...a,
                    source:
                      (a.kind === "audio" ? "sounds/" : "images/") +
                      names.get(a.id),
                  })),
              },
            }
          : {}),
      },
    });
  }
  if (pages.some((p) => p.data.liveDocument)) {
    const response = await fetch(
      "/templates/boxed/controllers/creatorenginepro/assets/enginepro/native-live-v77.js",
    );
    if (!response.ok)
      throw new Error(
        "Не удалось загрузить отрисовку живых элементов. Повторите экспорт.",
      );
    assets.set(
      "runtime/native-live.js",
      new Blob([await response.text()], { type: "text/javascript" }),
    );
  }
  return { pages, assets };
}
function liveBox(node: import("../scene/model").SceneNode) {
  const b = effectBounds(node),
    pad = 16;
  if ((b.width + pad * 2) * (b.height + pad * 2) * 4 > 64000000)
    throw new Error("Живой объект слишком велик для экспорта 2×.");
  return {
    x: Math.floor(b.x - pad),
    y: Math.floor(b.y - pad),
    width: Math.ceil(b.width + pad * 2),
    height: Math.ceil(b.height + pad * 2),
  };
}
function rgba(color: string, alpha: number) {
  return alpha === 1
    ? color
    : `rgba(${parseInt(color.slice(1, 3), 16)},${parseInt(color.slice(3, 5), 16)},${parseInt(color.slice(5, 7), 16)},${alpha})`;
}
export function materialHTML(page: ExportPage, inline?: Map<string, string>) {
  const data = structuredClone(page.data),
    elements = data.elements as Record<string, unknown>[];
  if (inline)
    for (const el of elements)
      for (const [key, folder] of [
        ["imageFileName", "images"],
        ["interactiveImageFileName", "images"],
        ["hoverSoundFileName", "sounds"],
        ["clickSoundFileName", "sounds"],
        ["releaseSoundFileName", "sounds"],
      ]) {
        const source = inline.get(folder + "/" + String(el[key!] || ""));
        if (source) el[key!] = source;
      }
  let fontCSS = String(data.fontCSS || "");
  delete data.fontCSS;
  if (inline) {
    data.nativePreview = true;
    for (const [name, source] of inline)
      if (name.startsWith("fonts/"))
        fontCSS = fontCSS.replaceAll(`url("${name}")`, `url("${source}")`);
  }
  if (data.liveDocument && inline) {
    for (const asset of (data.liveDocument as EngineDocument).assets)
      asset.source = inline.get(asset.source) || asset.source;
  }
  let html = buildHTMLFromExportData(data).replace(
    "<style>",
    "<style>" + fontCSS,
  );
  if (data.liveDocument)
    html = html.replace(
      "<script>",
      `<script src="${inline?.get("runtime/native-live.js") || "runtime/native-live.js"}"></script><script>`,
    );
  return html;
}
export const resultFiles = () => ({
  "save.php": buildSavePhp(),
  "results.php": buildResultsPhp(),
  "results.json": buildEmptyResultsJson(),
});
